/**
 * Friends Panel - Account-level friends list with presence (lobby right stack)
 *
 * Displays each friend with:
 * - Presence dot (green = online in the lobby, gray = offline)
 * - "In adventure" badge when the friend's party has an active game session
 * - A chat action that opens the account-level friend DM (online friends only)
 *
 * Friendships form via referral redemption or the "Add friend" affordance in
 * a 1:1 char chat; there is no accept step.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  MessageCircle,
  UserRound,
  Users,
  ArrowRight,
  ChevronUp,
  ChevronDown,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { usePanelCollapse } from '@/lib/hooks/usePanelCollapse';
import { websocketService } from '@/lib/websocket-service';
import { track } from '@/lib/analytics';
import { toast } from '@/lib/toast';
import type { FriendInfo } from '@danda/shared';

function FriendAvatar({ friend }: { friend: FriendInfo }) {
  if (friend.avatarUrl) {
    return (
      <img src={friend.avatarUrl} alt="" className="h-6 w-6 rounded-full object-cover shrink-0" />
    );
  }
  return (
    <span className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-xs shrink-0">
      {friend.avatarEmoji ?? <UserRound className="h-3.5 w-3.5 text-muted-foreground" />}
    </span>
  );
}

/**
 * Empty-state promo shown in place of the friends list when a player has no
 * friends yet: co-op is a headline feature, so the vacant panel becomes an
 * invitation to play together rather than dead space. Links to the full
 * /multiplayer guide (opened in a new tab so the lobby session is preserved).
 */
function MultiplayerPromo({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      data-testid="friends-panel-promo"
      className="relative select-none rounded-lg border border-accent-gold/40 bg-gradient-to-br from-amber-500/20 via-fuchsia-500/10 to-indigo-500/20 p-4 backdrop-blur-sm"
    >
      <button
        type="button"
        aria-label="Dismiss"
        className="absolute right-2 top-2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        onClick={onDismiss}
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-center gap-2 pr-6 text-sm font-semibold text-foreground">
        <Users className="h-4 w-4 text-accent-gold" />
        Play with friends
      </div>
      <ul className="mt-2 space-y-1.5 text-sm text-foreground/80 list-disc pl-5">
        <li>Team up with friends in a party</li>
        <li>Adventure together</li>
        <li>Split playing costs, much cheaper than solo play</li>
        <li>You both get lots of free playtime</li>
      </ul>
      <a
        href="/multiplayer"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-accent-gold hover:underline"
        onClick={() => track('multiplayer_promo_click', { surface: 'friends_panel_empty' })}
      >
        Learn more about multiplayer
        <ArrowRight className="h-3.5 w-3.5" />
      </a>
    </div>
  );
}

export default function FriendsPanel() {
  const { friends, friendsHydrated } = useLobbyStore(
    useShallow((s) => ({
      friends: s.friends,
      friendsHydrated: s.friendsHydrated,
    }))
  );
  const [isExpanded, toggleExpanded] = usePanelCollapse('friends-panel');
  // Owned here, not in MultiplayerPromo: the promo unmounts whenever the player
  // gains a friend, so state living inside it would reset the moment they lost
  // one again. Closing lasts for this lobby visit only, a reload brings it back.
  const [promoDismissed, setPromoDismissed] = useState(false);

  // The list is hydrated by the connection layer (requested on every socket
  // open, see websocket-service on-open) and kept warm by friend-added/removed
  // deltas, so the panel is purely presentational here.

  // Until the list has hydrated, "empty" is indistinguishable from "not loaded",
  // so render nothing rather than flash the promo at players who have friends.
  if (!friendsHydrated) {
    return null;
  }

  // No friends yet: promote co-op play instead of an empty list.
  if (friends.length === 0) {
    return promoDismissed ? null : <MultiplayerPromo onDismiss={() => setPromoDismissed(true)} />;
  }

  const handleFriendClick = (friend: FriendInfo) => {
    if (!friend.online) {
      toast.warning(`${friend.displayName} is offline. You can only chat with online friends.`);
      return;
    }
    websocketService.startFriendDm(friend.userId);
  };

  return (
    <Card data-testid="friends-panel">
      <CardHeader
        className="cursor-pointer select-none py-3"
        onClick={toggleExpanded}
        role="button"
        aria-expanded={isExpanded}
      >
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <UserRound className="h-4 w-4" />
            Your Friends
          </span>
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </CardTitle>
      </CardHeader>
      {isExpanded && (
        <CardContent className="pt-0 space-y-1">
          {friends.map((friend) => (
            <div
              key={friend.userId}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50 cursor-pointer select-none"
              role="button"
              aria-label={`Chat with ${friend.displayName}`}
              title={friend.online ? 'Start chat' : 'Friend is offline'}
              onClick={() => handleFriendClick(friend)}
            >
              <span
                aria-label={friend.online ? 'Online' : 'Offline'}
                className={`h-2 w-2 rounded-full shrink-0 ${
                  friend.online ? 'bg-green-500' : 'bg-muted-foreground/40'
                }`}
              />
              <FriendAvatar friend={friend} />
              <span className="text-sm truncate flex-1">{friend.displayName}</span>
              {friend.inSession && (
                <Badge variant="outline" className="h-5 px-1.5 text-[10px] shrink-0">
                  In adventure
                </Badge>
              )}
              <MessageCircle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}
